import { Edit } from '@mui/icons-material';
import { Chip, Switch } from '@mui/material';
import { AdminUsersApi } from '@/api/clients/admin-users/admin-users.api';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import Button from '@/components/CustomButtons/Button';
import GridContainer from '@/components/Grid/GridContainer';
import GridsItem from '@/components/Grid/GridsItem';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimestamp } from '@/utils/date/date';

import AdminUserFormModal from './components/AdminUserFormModal';

const AdminUserManagement = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [modalErr, setModalErr] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);

  const [inputs, setInputs] = useState({
    username: '',
    email: '',
    name: '',
    status: '',
    enabled: '',
  });
  const [permissions, setPermissions] = useState({});

  const onInputChange = (e, name) => {
    setInputs({ ...inputs, [name]: e.target.value });
  };

  const openAddAdminUserModal = () => {
    setOpen(true);
  };

  const addUser = async (e) => {
    try {
      e.preventDefault();
      await AdminUsersApi.createAdminUser({
        email: inputs.email,
        name: inputs.name,
        permissions,
      });

      getUsers();
      closeModal();
    } catch (err) {
      console.log(err);
      setModalErr(err.response?.data?.message);
    }
  };

  const updateUser = async (e) => {
    try {
      e.preventDefault();
      await AdminUsersApi.updateAdminUser({
        ...inputs,
        permissions,
      });
      getUsers();
      closeModal();
    } catch (err) {
      console.log(err);
      setModalErr(err.response?.data?.message);
    }
  };

  const deleteUser = async (e) => {
    try {
      e.preventDefault();
      await AdminUsersApi.deleteAdminUser(inputs.username);
      getUsers();
      closeModal();
    } catch (err) {
      console.log(err);
      setModalErr(err.response?.data?.message);
    }
  };

  const resendActivationEmail = async (e) => {
    try {
      e.preventDefault();
      await AdminUsersApi.resendActivationEmail(inputs.username);
      closeModal();
    } catch (err) {
      console.log(err);
      setModalErr(err.response?.data?.message);
    }
  };

  const toggleUser = async (username, deactivateUser) => {
    try {
      alert(
        `Are you sure you want to ${
          deactivateUser ? 'DEACTIVATE' : 'ACTIVATE'
        } this user?`,
      );

      await AdminUsersApi.toggleAdminUser(username, deactivateUser);
      getUsers();
    } catch (err) {
      console.log(err);
    }
  };

  const getUsers = async () => {
    try {
      const response = await AdminUsersApi.getAdminUsers();
      setAdminUsers(response.data ?? []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const updateUserData = async (user) => {
    try {
      const response = await AdminUsersApi.getAdminUser(user.username);
      const adminUser = response.data ?? user;

      setInputs({
        username: adminUser.username,
        email: adminUser.email,
        name: adminUser.name,
        status: adminUser.status,
        enabled: adminUser.enabled,
      });
      setPermissions(adminUser.permissions ?? {});
      setEditUser(true);
    } catch (err) {
      console.log(err);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setEditUser(null);
    setModalErr('');
    setInputs({
      username: '',
      email: '',
      name: '',
    });
    setPermissions({});
  };

  return (
    <GridContainer>
      <GridsItem xs={12} sm={12} md={12}>
        <h2 className='subHeading'>{t('adminUserManagement.title')}</h2>{' '}
      </GridsItem>
      <Card className='dashboardCard table_card_height'>
        <CardHeader>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              direction: 'row-reverse',
            }}>
            <Button
              color='primary'
              onClick={openAddAdminUserModal}
              style={{ height: '40px' }}>
              {t('adminUserManagement.addUserButton')}
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <table width='100%' id='template' className='paymentTable'>
            <thead>
              <tr>
                <th width='5%'>
                  {t('adminUserManagement.table.serialColumn')}
                </th>
                <th>{t('adminUserManagement.table.emailColumn')}</th>
                <th>{t('adminUserManagement.table.emailVerifiedColumn')}</th>
                <th>
                  {t('adminUserManagement.table.confirmationStatusColumn')}
                </th>
                <th>{t('adminUserManagement.table.statusColumn')}</th>
                <th>{t('adminUserManagement.table.lastSignInColumn')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {adminUsers.map((user, index) => {
                return (
                  <tr
                    key={user.username}
                    style={{ color: user.enabled === 'true' ? 'grey' : null }}>
                    <td>
                      <b>{index + 1}</b>
                    </td>
                    <td>
                      <b>{user.email}</b>
                    </td>
                    <td>
                      <Chip
                        label={user.emailVerified}
                        style={{ fontWeight: 600 }}
                        color={
                          user.emailVerified !== 'true' ? 'error' : 'success'
                        }
                      />
                    </td>
                    <td>
                      <Chip
                        label={user.status.toLowerCase()}
                        style={{ fontWeight: 600 }}
                        color={
                          user.status !== 'CONFIRMED' ? 'error' : 'success'
                        }
                      />
                    </td>
                    <td>
                      <Chip
                        label={
                          user.enabled === 'true' ? 'Activated' : 'Deactivated'
                        }
                        style={{ fontWeight: 600 }}
                        color={user.enabled === 'true' ? 'success' : 'error'}
                      />
                    </td>
                    <td>{formatTimestamp(user.last_sign_in_at)}</td>
                    <td>
                      <div
                        style={{
                          color: 'black',
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}>
                        <Switch
                          checked={user.enabled === 'true' ? true : false}
                          onChange={() =>
                            toggleUser(
                              user.username,
                              user.enabled === 'true' ? true : false,
                            )
                          }
                        />
                        <Edit
                          onClick={() => updateUserData(user)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <AdminUserFormModal
        open={Boolean(editUser) || open}
        isEdit={Boolean(editUser)}
        inputs={inputs}
        modalErr={modalErr}
        permissions={permissions}
        onPermissionsChange={setPermissions}
        onInputChange={onInputChange}
        onClose={closeModal}
        onSubmit={editUser ? updateUser : addUser}
        onResendActivationEmail={resendActivationEmail}
        onDelete={deleteUser}
      />
    </GridContainer>
  );
};

export default AdminUserManagement;
